import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { addDays, parseISO, isValid, differenceInCalendarDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { BookOpen, Calendar, LayoutGrid, LogOut, FileText, BookMarked, ShieldCheck, MessageCircle, CheckCircle2, PartyPopper, Share2, Info, Compass } from 'lucide-react';
import { toast } from 'sonner';
import ChildSelector from '@/components/ChildSelector';
import BrandMark from '@/components/BrandMark';
import FamilyLink from '@/components/FamilyLink';
import MessengerWidget from '@/components/MessengerWidget';
import { getTheme, sceneForActiveDays } from '@/config/termThemes';
import { shareScene } from '@/lib/shareScene';
import { useDashboardTour } from '@/hooks/useDashboardTour';
import DashboardTour from '@/components/DashboardTour';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

const TOUR_STEPS = [
  {
    targetTestId: 'dashboard-greeting',
    title: 'Welcome to your Dashboard!',
    body: "This is your home base — today's lesson and your child's progress garden, all in one place.",
  },
  {
    targetTestId: 'nav-lessons',
    title: 'Lessons',
    body: 'Browse every Maths and English lesson, any time — not just what\'s scheduled for today.',
  },
  {
    targetTestId: 'nav-planner',
    title: 'Planner',
    body: 'Plan out the whole term, week by week — drag lessons onto the days that work for your family.',
  },
  {
    targetTestId: 'nav-nest',
    title: 'The Nest',
    body: "See every child's whole day in one place, and add your own activities — swimming, appointments, anything — alongside their lessons.",
  },
  {
    targetTestId: 'nav-reports',
    title: 'Reports',
    body: 'Track progress over time and generate evidence reports for your records.',
  },
  {
    targetTestId: 'nav-reading-log',
    title: 'Reading Log',
    body: 'Log the books your child reads independently, outside their set lessons.',
  },
];

export default function Dashboard() {
  const { user, logout, authHeaders, childParam, activeChildName, activeChild, progressVersion } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState([]);
  const [planner, setPlanner] = useState(null);
  const [activeDays, setActiveDays] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [messengerOpen, setMessengerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const tour = useDashboardTour(TOUR_STEPS, user?.id);

  useEffect(() => {
    if (user?.id && !tour.hasSeenTour()) {
      tour.start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    const load = async () => {
      try {
        // /lessons and /progress were the only two calls here without a catch,
        // and their results go straight into .filter(). Anything that isn't an
        // array — a failed request, or a body axios couldn't parse as JSON and
        // handed back as a string — threw during render and blanked the whole
        // page. Catch both, and never trust the shape of what comes back.
        const [lessonsRes, progressRes, plannerRes, evidenceRes, messagesRes] = await Promise.all([
          axios.get(`${API}/lessons`).catch(() => ({ data: null })),
          axios.get(`${API}/progress${childParam}`, { headers: authHeaders }).catch(() => ({ data: null })),
          axios.get(`${API}/planner${childParam}`, { headers: authHeaders }).catch(() => ({ data: null })),
          axios.get(`${API}/reports/evidence${childParam}`, { headers: authHeaders }).catch(() => ({ data: null })),
          axios.get(`${API}/messages`, { headers: authHeaders }).catch(() => ({ data: [] })),
        ]);
        const asList = (v) => (Array.isArray(v) ? v : []);
        setLessons(asList(lessonsRes.data));
        setProgress(asList(progressRes.data));
        setLoadFailed(!Array.isArray(lessonsRes.data) || !Array.isArray(progressRes.data));
        setPlanner(plannerRes.data && typeof plannerRes.data === 'object' ? plannerRes.data : null);
        const byTerm = evidenceRes.data?.summary?.active_days_by_term || {};
        const yearLevel = activeChild?.year_level || 5;
        const termNumber = plannerRes.data?.term_number || 1;
        setActiveDays(Number(byTerm[`${yearLevel}-${termNumber}`] || 0));
        setUnreadCount((Array.isArray(messagesRes.data) ? messagesRes.data : []).filter(m => m.unread).length);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [authHeaders, childParam, progressVersion, activeChild]);

  const termNumber = planner?.term_number || 1;
  const yearLevel = activeChild?.year_level || 5;
  const theme = getTheme(yearLevel, termNumber);
  const sceneIndex = sceneForActiveDays(theme, activeDays);
  const reachedMilestones = theme.milestones.filter(m => activeDays >= m.count);
  const nextMilestone = theme.milestones.find(m => activeDays < m.count);
  const latestMilestone = reachedMilestones[reachedMilestones.length - 1];

  const termLessons = lessons.filter(l => (l.term ?? 1) === termNumber && (l.year ?? 5) === yearLevel);
  const termRefs = new Set(termLessons.map(l => l.reference));
  const completedRefs = new Set(progress.filter(p => p.completed).map(p => p.lesson_reference));
  const completedCount = [...completedRefs].filter(r => termRefs.has(r)).length;
  const totalLessons = termLessons.length;
  const termComplete = totalLessons > 0 && completedCount === totalLessons;

  // Term start is the Monday of Week 1 — same convention as PlannerPage.
  const termStart = planner?.term_start ? parseISO(planner.term_start) : null;
  const todayCell = (() => {
    if (!termStart || !isValid(termStart)) return null;
    const diff = differenceInCalendarDays(new Date(), termStart);
    const week = Math.floor(diff / 7) + 1;
    const day = (diff % 7) + 1; // 1=Mon..7=Sun
    if (diff < 0 || day > 5 || week > (planner?.weeks || 0)) return null;
    return { week, day };
  })();

  // Same reasoning as lessons/progress: never assume the API's shape.
  const plannerSlots = Array.isArray(planner?.slots) ? planner.slots : [];
  const lessonByRef = (ref) => lessons.find(l => l.reference === ref);
  const todaySlots = todayCell
    ? plannerSlots.filter(s => s.week === todayCell.week && s.day === todayCell.day)
    : [];
  const todayLessons = todaySlots.map(s => lessonByRef(s.lesson_reference)).filter(Boolean);
  const todayIncomplete = todayLessons.filter(l => !completedRefs.has(l.reference));
  const todayAllDone = todayLessons.length > 0 && todayIncomplete.length === 0;
  const primaryLesson = todayIncomplete[0];

  // This week's Mon-Fri strip, for context beyond "today".
  const weekCells = todayCell
    ? [1, 2, 3, 4, 5].map(day => {
        const slots = plannerSlots.filter(s => s.week === todayCell.week && s.day === day);
        return { day, isToday: day === todayCell.day, lessons: slots.map(s => lessonByRef(s.lesson_reference)).filter(Boolean) };
      })
    : [];

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const result = await shareScene({
        assetPrefix: theme.assetPrefix,
        sceneIndex,
        themeName: theme.name,
        teReo: theme.teReo,
        childName: activeChildName,
        activeDays,
        latestLabel: latestMilestone?.label,
        shareSubtitle: theme.shareSubtitle,
        year: yearLevel,
      });
      if (result === 'downloaded') toast.success(`${theme.name} saved! Check your downloads.`);
      else if (result === 'shared') toast.success('Shared with whānau 🌿');
    } catch {
      toast.error('Could not share right now — try again in a moment');
    }
    setSharing(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-sand flex items-center justify-center text-slate-400">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center flex-shrink-0">
            <BrandMark size={36} />
          </Link>
          <nav className="flex items-center gap-2 sm:gap-4 overflow-x-auto">
            <Link to="/lessons" data-testid="nav-lessons">
              <Button variant="ghost" className="text-slate-600 hover:text-fern hover:bg-fern/5 rounded-lg gap-2">
                <BookOpen className="w-4 h-4" /> <span className="hidden sm:inline">Lessons</span>
              </Button>
            </Link>
            <Link to="/planner" data-testid="nav-planner">
              <Button variant="ghost" className="text-slate-600 hover:text-fern hover:bg-fern/5 rounded-lg gap-2">
                <Calendar className="w-4 h-4" /> <span className="hidden sm:inline">Planner</span>
              </Button>
            </Link>
            <Link to="/nest" data-testid="nav-nest">
              <Button variant="ghost" className="text-slate-600 hover:text-fern hover:bg-fern/5 rounded-lg gap-2">
                <LayoutGrid className="w-4 h-4" /> <span className="hidden sm:inline">Nest</span>
              </Button>
            </Link>
            <Link to="/reports" data-testid="nav-reports">
              <Button variant="ghost" className="text-slate-600 hover:text-fern hover:bg-fern/5 rounded-lg gap-2">
                <FileText className="w-4 h-4" /> <span className="hidden sm:inline">Reports</span>
              </Button>
            </Link>
            <Link to="/reading-log" data-testid="nav-reading-log">
              <Button variant="ghost" className="text-slate-600 hover:text-fern hover:bg-fern/5 rounded-lg gap-2">
                <BookMarked className="w-4 h-4" /> <span className="hidden sm:inline">Reading</span>
              </Button>
            </Link>
            {user?.is_admin && (
              <Link to="/admin" data-testid="nav-admin">
                <Button variant="ghost" className="text-slate-600 hover:text-fern hover:bg-fern/5 rounded-lg gap-2">
                  <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline">Admin</span>
                </Button>
              </Link>
            )}
            <FamilyLink />
            <ChildSelector />
            <Button
              variant="ghost"
              onClick={tour.start}
              className="text-slate-400 hover:text-fern rounded-lg"
              data-testid="take-tour-btn"
              title="Take the tour"
              aria-label="Take the tour"
            >
              <Compass className="w-4 h-4" />
            </Button>
            <Button data-testid="logout-btn" variant="ghost" onClick={logout} className="text-slate-400 hover:text-red-500 rounded-lg">
              <LogOut className="w-4 h-4" />
            </Button>
          </nav>
        </div>
      </header>

      <main>
        {loadFailed && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-6" data-testid="dashboard-load-failed">
            <div className="rounded-2xl border border-kowhai/40 bg-kowhai/10 px-4 py-3 text-sm text-fern-dark">
              We couldn't load your lessons or progress just now, so this page may look emptier than
              it should. Your work is safe —{' '}
              <button onClick={() => window.location.reload()} className="font-semibold underline">
                try reloading
              </button>.
            </div>
          </div>
        )}

        {/* Today's focus — plain text band, no overlay */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-14 sm:pt-16 pb-10">
          <div className="max-w-2xl">
            <div className="text-sm font-semibold text-moana uppercase tracking-wide mb-4">
              Term {termNumber}{todayCell ? ` · Week ${todayCell.week}` : ''}
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl font-bold text-fern-dark leading-[1.05]" data-testid="dashboard-greeting">
              Kia ora{activeChildName ? `, ${activeChildName}` : (user?.family_name ? `, ${user.family_name}` : '')}.
            </h1>

            {!planner && (
              <>
                <p className="text-lg text-fern font-medium mt-1">Let's set up your term.</p>
                <Button className="mt-6 bg-fern hover:bg-fern-dark text-white rounded-xl gap-2 h-auto py-3 px-6" onClick={() => navigate('/planner')} data-testid="setup-planner-btn">
                  Set up your Planner
                </Button>
              </>
            )}

            {planner && termComplete && (
              <>
                <p className="text-lg text-fern font-medium mt-1 flex items-center gap-2">
                  <PartyPopper className="w-5 h-5" /> You've finished Term {termNumber}!
                </p>
                <p className="text-sm text-slate-500 mt-3">{completedCount}/{totalLessons} lessons complete. Time to plan the next term.</p>
              </>
            )}

            {planner && !termComplete && !todayCell && (
              <>
                <p className="text-lg text-fern font-medium mt-1">Nothing scheduled today — enjoy the break!</p>
                <p className="text-sm text-slate-500 mt-3">Check this week's plan in the Planner.</p>
              </>
            )}

            {planner && !termComplete && todayCell && todayLessons.length === 0 && (
              <>
                <p className="text-lg text-fern font-medium mt-1">No lessons scheduled for today.</p>
                <Button variant="outline" className="mt-6 rounded-xl gap-2" onClick={() => navigate('/planner')} data-testid="open-planner-btn">
                  Open Planner
                </Button>
              </>
            )}

            {planner && !termComplete && todayCell && todayAllDone && (
              <p className="text-lg text-fern font-medium mt-1 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" /> All done for today — great mahi!
              </p>
            )}

            {planner && !termComplete && primaryLesson && (
              <>
                <p className="text-2xl sm:text-3xl text-fern font-heading font-medium mt-2">{primaryLesson.title}</p>
                <div className="mt-9">
                  <Button
                    className="bg-fern hover:bg-fern-dark text-white rounded-xl gap-2 h-auto py-4 px-8 text-lg font-semibold shadow-lg shadow-fern/30"
                    onClick={() => navigate(`/lessons/${primaryLesson.reference}`)}
                    data-testid="start-today-lesson-btn"
                  >
                    Start today's lesson
                  </Button>
                </div>
                <p className="text-sm text-slate-500 mt-4">{primaryLesson.teReoTitle} · {primaryLesson.duration} min</p>
                {todayIncomplete.length > 1 && (
                  <p className="text-sm text-slate-400 mt-1">+{todayIncomplete.length - 1} more scheduled today</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* The garden — the child's actual term-theme scene, shown in full, unobstructed, edge-to-edge */}
        {theme.available && (
          <div className="relative">
            <img
              src={`${theme.assetPrefix}_${sceneIndex}.png`}
              alt={`${theme.name} — ${latestMilestone ? latestMilestone.label : 'just getting started'}`}
              className="w-full h-[320px] sm:h-[480px] lg:h-[560px] object-cover"
              data-testid="hero-theme-scene"
            />
            <div className="absolute right-4 sm:right-6 lg:right-10 top-4 sm:top-6 flex flex-col items-end gap-2">
              {latestMilestone && (
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-2 text-right shadow-sm max-w-[220px] sm:max-w-xs">
                  <div className="text-xs font-semibold text-fern-dark">🌿 {latestMilestone.label}</div>
                  <div className="text-[11px] text-slate-500 italic mt-0.5">{latestMilestone.teReo}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{activeDays} active {activeDays === 1 ? 'day' : 'days'}</div>
                </div>
              )}
              <button
                onClick={handleShare}
                disabled={sharing}
                data-testid="share-garden-btn"
                className="bg-white/90 backdrop-blur-sm rounded-full p-2.5 shadow-sm text-fern-dark hover:bg-white disabled:opacity-60"
                aria-label="Share your garden"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
            {!latestMilestone && (
              <div className="absolute left-4 sm:left-6 lg:left-10 bottom-4 sm:bottom-6 bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-2.5 shadow-sm max-w-[260px] sm:max-w-sm">
                <div className="text-xs font-semibold text-fern-dark">{theme.name} is just getting started</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{theme.emptyCopy}</div>
              </div>
            )}
            {latestMilestone?.note && (
              <div className="absolute left-4 sm:left-6 lg:left-10 bottom-4 sm:bottom-6 max-w-[280px] sm:max-w-sm">
                {!noteOpen ? (
                  <button
                    onClick={() => setNoteOpen(true)}
                    data-testid="learn-about-scene-btn"
                    className="bg-white/90 backdrop-blur-sm rounded-full pl-2.5 pr-3.5 py-2 shadow-sm text-xs font-semibold text-fern-dark flex items-center gap-1.5 hover:bg-white"
                  >
                    <Info className="w-3.5 h-3.5" /> Learn about this scene
                  </button>
                ) : (
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-sm">
                    <button onClick={() => setNoteOpen(false)} className="text-[11px] font-semibold text-fern-dark flex items-center gap-1.5 mb-1.5">
                      <Info className="w-3.5 h-3.5" /> {latestMilestone.label}
                    </button>
                    <p className="text-[11px] text-slate-600 leading-relaxed">{latestMilestone.note}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Terms whose scene art has not shipped yet still have a name, a te reo
            title and a description — so show those rather than leaving the
            dashboard's whole centre empty. Before this, Year 6 T2-T4 and Year 7
            T3-T4 families reached a term with no art and got a blank page. */}
        {!theme.available && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10" data-testid="theme-coming-soon">
            <div className="rounded-3xl border border-sand-darker bg-gradient-to-br from-fern/5 via-moana/5 to-kowhai/10 px-6 py-8 sm:px-10 sm:py-10">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-fern-dark">{theme.name}</h2>
                {theme.teReo && <span className="text-base text-moana italic">{theme.teReo}</span>}
              </div>
              {theme.description && (
                <p className="text-slate-600 mt-3 leading-relaxed max-w-2xl">{theme.description}</p>
              )}
              {/* The name and description already say the scene is coming, so
                  this line only adds what they don't: progress is still counted. */}
              <p className="text-sm text-slate-500 mt-5">
                Your progress is still being counted — you have{' '}
                <span className="font-semibold text-fern-dark">{activeDays} active {activeDays === 1 ? 'day' : 'days'}</span>{' '}
                this term. The artwork will appear here once it lands.
              </p>
            </div>
          </div>
        )}

        {/* Status line */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-10">
          <div className="border-t border-sand-darker pt-6 flex items-baseline justify-between flex-wrap gap-3">
            <div className="text-base text-slate-500" data-testid="dashboard-term-label">
              <span className="text-fern-dark font-semibold">{completedCount} of {totalLessons}</span> lessons done this term
              {theme.available && nextMilestone && (
                <> · next scene at <span className="text-moana font-semibold">{nextMilestone.count} active days</span> ({nextMilestone.label})</>
              )}
            </div>
            <button
              className="text-base text-moana font-semibold flex items-center gap-1.5"
              onClick={() => setMessengerOpen(true)}
              data-testid="open-messenger-btn"
            >
              <MessageCircle className="w-5 h-5" />
              {unreadCount > 0 ? `${unreadCount} new message${unreadCount > 1 ? 's' : ''}` : 'Messages'}
            </button>
          </div>
        </div>

        {/* This week */}
        {weekCells.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-16">
            <div className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-4">This week</div>
            <div className="grid grid-cols-5 gap-px bg-sand-darker rounded-2xl overflow-hidden">
              {weekCells.map(cell => (
                <div key={cell.day} className={`bg-white p-4 sm:p-5 ${cell.isToday ? 'ring-2 ring-inset ring-fern bg-fern-50' : ''}`} data-testid={`week-cell-${cell.day}`}>
                  <div className={`text-xs uppercase mb-2 ${cell.isToday ? 'text-fern-dark font-bold' : 'text-slate-400'}`}>
                    {cell.isToday ? 'Today' : DAY_SHORT[cell.day - 1]}
                  </div>
                  {cell.lessons.length === 0 && <div className="text-sm text-slate-300">—</div>}
                  {cell.lessons.map(l => (
                    <div key={l.reference} className={`text-sm truncate ${completedRefs.has(l.reference) ? 'text-slate-400 line-through' : l.subject === 'maths' ? 'text-fern font-medium' : 'text-moana font-medium'}`}>
                      {l.title}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <Link to="/planner" className="inline-block mt-3 text-sm text-moana font-semibold" data-testid="open-full-planner-link">
              Open full planner →
            </Link>
          </div>
        )}
      </main>

      <Sheet open={messengerOpen} onOpenChange={setMessengerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0">
          <SheetHeader className="p-4 border-b">
            <SheetTitle>Messages</SheetTitle>
          </SheetHeader>
          <div className="p-4">
            <MessengerWidget />
          </div>
        </SheetContent>
      </Sheet>

      <DashboardTour tour={tour} />
    </div>
  );
}
